import { dateFNS, toDate } from '@gogame/intl-ts';
import {
  SET_FILTERS,
  SET_TABLE_SETTINGS,
  APPLY_PRESET,
  CHANGE_LOCATION,
} from '@gogame/screens';

import {
  COLUMN_DIMENSION,
  DIMENSION_DAY,
  DIMENSION_MONTH,
  DIMENSION_WEEK,
} from '../constants';
import { mapPresetToState, tableSettingsSchema } from '../schemas';
import { clearBreakdownPathOfOpenRows } from './breakdownPathReducer';
import {
  breakdownPathReducer,
  ADD_BREAKDOWN_PATH,
  TOGGLE_BREAKDOWN_EXPANDED,
  COLLAPSE_BREAKDOWN_EXPANDED,
  CHANGE_BREAKDOWN_PATH,
  REMOVE_BREAKDOWN_PATH,
  OPEN_MULTIPLE_ROWS,
} from './breakdownPathReducer';
import {
  chartsReducer,
  REMOVE_CHART,
  ADD_CHART,
  DUPLICATE_CHART,
  SET_CHARTS_WIDTHS,
  CHANGE_CART_LOADING_TYPE,
  CHART_ACTIVATE_BREAKDOWN_MODE,
  CHART_DEACTIVATE_BREAKDOWN_MODE,
  SHOW_CHART_ANNOTATIONS,
  HIDE_CHART_ANNOTATIONS,
  SET_CHART_BREAKDOWN,
  SET_CHART_METRICS,
  SET_CHART_SETTINGS,
  SET_CHART_VIEW_SETTINGS,
  SET_CHARTS_LOADING_TYPE,
  SET_CHARTS_DEACTIVATE_BREAKDOWN_MODE,
} from './chartsReducer';
import {
  columnReducer,
  SET_COLOR_SCALES_ALL_COLUMNS,
  SET_COLOR_SCALES_COLUMNS,
  SET_COLUMNS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  TOGGLE_COLUMN_PERCENTAGE,
} from './columnReducer';
import {
  drillPathReducer,
  SET_DIMENSION,
  SET_DRILL_PATH,
  DRILL_DOWN,
  SET_BREAKDOWN_DIMENSIONS,
} from './drillPathReducer';

export const APPLY = 'APPLY';
export const SET_ADVANCED_FILTERS = 'SET_ADVANCED_FILTERS';
export const SET_PRESENTED_ROWS = 'SET_PRESENTED_ROWS'; // presentedRows
export const SET_DIMENSION_SEARCH_QUERY = 'SET_DIMENSION_SEARCH_QUERY';
export const SET_COHORTS_SETTINGS = 'SET_COHORTS_SETTINGS';
export const SET_NEXT_DIMENSION = 'SET_NEXT_DIMENSION';
export const CLEAR_DIMENSION = 'CLEAR_DIMENSION';
export const CANCEL_LOADING = 'CANCEL_LOADING';
export const SWAP_DIMENSIONS = 'SWAP_DIMENSIONS';

export {
  APPLY_PRESET,
  // charts
  REMOVE_CHART,
  ADD_CHART,
  DUPLICATE_CHART,
  SET_CHARTS_WIDTHS,
  CHANGE_CART_LOADING_TYPE,
  CHART_ACTIVATE_BREAKDOWN_MODE,
  CHART_DEACTIVATE_BREAKDOWN_MODE,
  SHOW_CHART_ANNOTATIONS,
  HIDE_CHART_ANNOTATIONS,
  SET_CHART_BREAKDOWN,
  SET_CHART_METRICS,
  SET_CHART_SETTINGS,
  SET_CHART_VIEW_SETTINGS,
  // filters
  SET_FILTERS,
  // columns
  SET_COLOR_SCALES_ALL_COLUMNS,
  SET_COLOR_SCALES_COLUMNS,
  SET_COLUMNS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  TOGGLE_COLUMN_PERCENTAGE,
  // breakdown path
  ADD_BREAKDOWN_PATH,
  TOGGLE_BREAKDOWN_EXPANDED,
  COLLAPSE_BREAKDOWN_EXPANDED,
  CHANGE_BREAKDOWN_PATH,
  REMOVE_BREAKDOWN_PATH,
  OPEN_MULTIPLE_ROWS,
  //drill path
  SET_DIMENSION,
  SET_DRILL_PATH,
  DRILL_DOWN,
  SET_BREAKDOWN_DIMENSIONS,
};

export function trackerReducer(state, action) {
  switch (action.type) {
    case SWAP_DIMENSIONS: {
      const { left, right } = action.payload;
      let nextState = state;
      if (left.level === -1) {
        nextState = _trackerReducer(state, {
          type: SET_DIMENSION,
          payload: right.value,
        });
      } else {
        nextState = _trackerReducer(nextState, {
          type: CHANGE_BREAKDOWN_PATH,
          payload: { index: left.level, breakdownId: right.value },
        });
      }

      nextState = _trackerReducer(nextState, {
        type: CHANGE_BREAKDOWN_PATH,
        payload: { index: right.level, breakdownId: left.value },
      });

      return nextState;
    }
    default:
      return _trackerReducer(state, action);
  }
}

function _trackerReducer(state = { ready: false }, action) {
  switch (action.type) {
    case APPLY: {
      let { loadingCanceled } = action.payload;

      return {
        ...action.payload,
        forceReloadId: loadingCanceled ? +new Date() : state.forceReloadId,
      };
    }

    case APPLY_PRESET:
      if (action.payload) {
        const { id, data } = action.payload;
        const { applyPresetData } = action.meta;

        // if applyPresetData is 'false', it is new preset and it's data equal to current state
        return {
          ...(applyPresetData ? mapPresetToState(data) : state),
          ready: true,
          presetId: id,
        };
      }
      // Active preset was removed
      return {
        ...state,
        // clean activePresetId
        presetId: undefined,
      };
    case CHANGE_LOCATION:
      return action.payload;

    case CANCEL_LOADING: {
      return {
        ...state,
        loadingCanceled: true,
        changes: {}, // not empty to enable Apply button
      };
    }
    // drill path

    case SET_BREAKDOWN_DIMENSIONS: {
      return {
        ...state,
        drillPath: drillPathReducer(state.drillPath, action),
      };
    }
    case CLEAR_DIMENSION:
      return trackerReducer(
        trackerReducer(state, {
          type: REMOVE_BREAKDOWN_PATH,
          payload: { index: 0 },
        }),
        {
          type: SET_DIMENSION,
          payload: state.breakdownPath[0].breakdownId,
        }
      );

    case SET_DRILL_PATH:
    case DRILL_DOWN:
    case SET_DIMENSION:
      const keepBreakdownPath = [SET_DRILL_PATH, SET_DIMENSION].includes(
        action.type
      );

      const newDrillPath = drillPathReducer(state.drillPath, action);
      const nextDimension = getDimensionType(newDrillPath);

      let nextState = {
        ...state,
        ...changeDefinedProp(state, 'presentedRows', []),
        ...changeDefinedProp(state, 'advancedFilters', []),
        ...changeDefinedProp(state, 'dimensionSearchQuery', ''),
        ...changeDefinedProp(
          state,
          'breakdownPath',
          keepBreakdownPath
            ? clearBreakdownPathOfOpenRows(state.breakdownPath)
            : []
        ),
        ...changeDefinedProp(state, 'drillPath', newDrillPath),
        tableSettings: {
          ...state.tableSettings,
          offset: 0,
        },
      };

      if (isDateDimension(nextDimension)) {
        // current dimension is one of date dimensions
        nextState = trackerReducer(nextState, {
          type: SET_CHARTS_DEACTIVATE_BREAKDOWN_MODE,
        });

        nextState = trackerReducer(nextState, {
          type: SET_TABLE_SETTINGS,
          payload: {
            orderBy: COLUMN_DIMENSION,
            orderDir: 'asc',
          },
        });
      } else if (isDateDimension(getDimensionType(state.drillPath))) {
        // previous dimension is one of date dimensions

        nextState = trackerReducer(nextState, {
          type: SET_TABLE_SETTINGS,
          payload: {
            ...tableSettingsSchema.default(),
            limit: nextState.tableSettings.limit,
          },
        });
      }

      return ensureSorting(nextState);
    // breakdown path
    case ADD_BREAKDOWN_PATH:
    case TOGGLE_BREAKDOWN_EXPANDED:
    case COLLAPSE_BREAKDOWN_EXPANDED:
    case CHANGE_BREAKDOWN_PATH:
    case OPEN_MULTIPLE_ROWS:
    case REMOVE_BREAKDOWN_PATH: {
      return {
        ...state,
        breakdownPath: breakdownPathReducer(state.breakdownPath, action),
        // breakdownPathChanges: action.payload,
      };
    }

    // columns
    case SET_COLUMNS: {
      return ensureSorting({
        ...state,
        columns: columnReducer(state.columns, action),
      });
    }
    case COLLAPSE_COLUMNS:
    case SET_COLOR_SCALES_ALL_COLUMNS:
    case SET_COLOR_SCALES_COLUMNS:
    case REORDER_COLUMNS:
    case TOGGLE_COLUMN_PERCENTAGE:
      return {
        ...state,
        columns: columnReducer(state.columns, action),
      };

    // charts
    case ADD_CHART:
    case REMOVE_CHART:
    case DUPLICATE_CHART:
    case SET_CHARTS_WIDTHS:
    case SET_CHART_SETTINGS:
    case SET_CHART_METRICS:
    case SET_CHART_BREAKDOWN:
    case CHART_ACTIVATE_BREAKDOWN_MODE:
    case CHART_DEACTIVATE_BREAKDOWN_MODE:
    case SET_CHARTS_DEACTIVATE_BREAKDOWN_MODE:
    case SET_CHART_VIEW_SETTINGS:
    case CHANGE_CART_LOADING_TYPE:
    case SHOW_CHART_ANNOTATIONS:
    case HIDE_CHART_ANNOTATIONS: {
      return {
        ...state,
        charts: state.charts
          ? chartsReducer(state.charts, action)
          : state.charts,
      };
    }
    case SET_FILTERS: {
      let nextState = {
        ...state,
        filtersCollection: action.payload,
      };

      nextState = trackerReducer(nextState, {
        type: SET_TABLE_SETTINGS,
        payload: {
          offset: tableSettingsSchema.default().offset,
        },
      });

      if (
        somePeriodChanged(nextState.filtersCollection, state.filtersCollection)
      ) {
        nextState = trackerReducer(nextState, {
          type: SET_CHARTS_LOADING_TYPE,
          payload: somePeriodLong(nextState.filtersCollection),
        });
      }

      return ensureSorting({
        ...nextState,
        breakdownPath: clearBreakdownPathOfOpenRows(state.breakdownPath),
      });
    }

    case SET_PRESENTED_ROWS:
      return {
        ...state,
        breakdownPath: clearBreakdownPathOfOpenRows(state.breakdownPath),
        presentedRows: action.payload,
      };

    case SET_ADVANCED_FILTERS:
      return {
        ...state,
        breakdownPath: clearBreakdownPathOfOpenRows(state.breakdownPath),
        advancedFilters: action.payload,
        tableSettings: {
          ...state.tableSettings,
          offset: 0,
        },
      };

    case SET_DIMENSION_SEARCH_QUERY:
      return {
        ...state,
        dimensionSearchQuery: action.payload,
        tableSettings: {
          ...state.tableSettings,
          offset: 0,
        },
      };

    case SET_COHORTS_SETTINGS:
      return {
        ...state,
        cohortsSettings: action.payload,
      };

    case SET_TABLE_SETTINGS: {
      let nextTableSettings = { ...state.tableSettings, ...action.payload };

      return {
        ...state,
        tableSettings: nextTableSettings,
      };
    }

    case SET_NEXT_DIMENSION:
      return {
        ...state,
        nextDimension: action.payload,
      };

    default:
      return state;
  }
}

function ensureSorting(state) {
  const order = state?.columns?.order;

  if (
    !order ||
    [COLUMN_DIMENSION, ...order].includes(state?.tableSettings?.orderBy)
  ) {
    return state;
  }

  return {
    ...state,
    tableSettings: {
      ...state.tableSettings,
      orderBy: order[0], //sort by first column
      orderDir: 'desc',
      offset: 0,
    },
  };
}

export function somePeriodChanged(filtersCollection, otherFiltersCollection) {
  return filtersCollection.some(({ period }, i) => {
    const prevPeriod = otherFiltersCollection?.[i]?.period;

    return (
      !period ||
      !prevPeriod ||
      period.some((date, j) => {
        const prevDate = prevPeriod[j];
        return !dateFNS.isEqual(new Date(date), prevDate);
      })
    );
  });
}

export function somePeriodLong(filtersCollection) {
  return filtersCollection.some(({ period }) => {
    return (
      Math.abs(
        dateFNS.differenceInYears(...period.map((dateStr) => toDate(dateStr)))
      ) >= 2
    );
  });
}
function getDimensionType(drillPath) {
  return drillPath[drillPath.length - 1]?.type;
}

function isDateDimension(dimensionType) {
  return [DIMENSION_DAY, DIMENSION_MONTH, DIMENSION_WEEK].includes(
    dimensionType
  );
}

function changeDefinedProp(source, prop, emptyValue) {
  return source[prop] === undefined ? null : { [prop]: emptyValue };
}
